import { TLegalDocument, legalService } from "@/api/legal";
import {
  DOCUMENT_TYPE_LABELS,
  LEGAL_DOCUMENT_EDIT_FORM_ID,
  LEGAL_DOCUMENT_WORKSPACE_MIN_HEIGHT,
  LegalDocumentForm,
  LegalDocumentPreviewDialog,
  LegalDocumentStatusBadge,
  toDateInputValue,
} from "@/components/admin/legal";
import { Button, RouteLink, useToast } from "@/components/ui";
import { ArrowLeft, Eye } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { TLegalDocumentEditFormValues } from "@/api/legal/legal.types";
import { dateInputToIso } from "@/components/admin/legal/constants";
import { AdminPageLayout } from "@/components/layout/AdminPageLayout";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { AppCard } from "@/components/ui/AppCard";
import { extractErrorMessage } from "@/utils";

type TProps = {
  documentId: string;
};

export function LegalDocumentDetailPage({ documentId }: TProps) {
  const { toast } = useToast();
  const [document, setDocument] = useState<TLegalDocument | null>(null);
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const loadDocument = useCallback(async () => {
    setIsLoading(true);
    const { error, data } = await legalService.getDocumentById(documentId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to load document",
        description: await extractErrorMessage(error),
      });
      setDocument(null);
    } else if (data?.data) {
      setDocument(data.data);
      setPreviewContent(data.data.content);
      setPreviewTitle(data.data.title);
    }

    setIsLoading(false);
  }, [documentId, toast]);

  useEffect(() => {
    void loadDocument();
  }, [loadDocument]);

  const handleSave = async (values: TLegalDocumentEditFormValues) => {
    setIsSaving(true);
    const { error, data } = await legalService.updateDocument(documentId, {
      title: values.title,
      content: values.content,
      effective_at: dateInputToIso(values.effective_at),
    });
    setIsSaving(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not save changes",
        description: await extractErrorMessage(error),
      });
      return;
    }

    toast({ variant: "success", title: "Document updated" });
    if (data?.data) {
      setDocument(data.data);
      setPreviewContent(data.data.content);
      setPreviewTitle(data.data.title);
    }
  };

  const handleActivate = () => {
    if (!document) return;
    setActivateConfirmOpen(true);
  };

  const confirmActivate = async () => {
    setIsActivating(true);
    const { error, data } = await legalService.activateDocument(documentId);
    setIsActivating(false);
    setActivateConfirmOpen(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Could not activate document",
        description: await extractErrorMessage(error),
      });
      return;
    }

    toast({ variant: "success", title: "Document activated" });
    if (data?.data) {
      setDocument(data.data);
    }
  };

  if (isLoading) {
    return (
      <AdminPageLayout
        title="Legal document"
        subtitle="Loading document details..."
      >
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </AdminPageLayout>
    );
  }

  if (!document) {
    return (
      <AdminPageLayout
        title="Legal document"
        subtitle="Document not found."
      >
        <RouteLink to="/app/admin/legal">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to documents
          </Button>
        </RouteLink>
      </AdminPageLayout>
    );
  }

  const typeLabel = DOCUMENT_TYPE_LABELS[document.document_type];

  return (
    <AdminPageLayout
      title={typeLabel}
      maxWidth="7xl"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Version {document.version}</span>
          <LegalDocumentStatusBadge isActive={document.is_active} />
          {!document.is_active ? (
            <Button
              onClick={handleActivate}
              loading={isActivating}
              disabled={isActivating}
            >
              Activate version
            </Button>
          ) : null}
          <RouteLink to="/app/admin/legal">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to documents
            </Button>
          </RouteLink>
        </div>
      }
    >
      <AppCard
        variant="elevated"
        padding="none"
        className={`flex min-h-0 flex-col overflow-hidden ${LEGAL_DOCUMENT_WORKSPACE_MIN_HEIGHT}`}
      >
        <div
          className="sticky top-0 z-10 flex shrink-0 items-center justify-end gap-2 border-b border-border bg-card px-6 py-3 sm:px-8"
        >
          <Button type="button" variant="outline" onClick={() => setIsPreviewOpen(true)}>
            <Eye className="mr-2 h-4 w-4" />
            Live preview
          </Button>
          <Button
            type="submit"
            form={LEGAL_DOCUMENT_EDIT_FORM_ID}
            loading={isSaving}
            disabled={isSaving}
          >
            Save changes
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-6 py-5 sm:px-8 sm:py-6">
          <LegalDocumentForm
            mode="edit"
            fillEditor
            formId={LEGAL_DOCUMENT_EDIT_FORM_ID}
            defaultValues={{
              title: document.title,
              content: document.content,
              effective_at: toDateInputValue(document.effective_at),
            }}
            readOnlyFields={{
              document_type: document.document_type,
              version: document.version,
            }}
            onSubmit={async (values) => {
              await handleSave(values);
            }}
            onPreviewChange={({ title, content }) => {
              setPreviewTitle(title);
              setPreviewContent(content);
            }}
            isSubmitting={isSaving}
          />
        </div>
      </AppCard>

      <LegalDocumentPreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        title={previewTitle}
        content={previewContent}
        version={document.version}
        effectiveAt={document.effective_at}
      />

      <ConfirmDialog
        open={activateConfirmOpen}
        onOpenChange={setActivateConfirmOpen}
        title="Activate document?"
        description={`Activate "${document.title}" (v${document.version})? This will deactivate other active versions of the same document type.`}
        confirmLabel="Activate"
        variant="default"
        isConfirming={isActivating}
        onConfirm={confirmActivate}
      />
    </AdminPageLayout>
  );
}
